import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Briefcase, Users, Plus, TrendingUp, Sparkles, ArrowRight,
  Loader, Mail, Trash2, RefreshCw, Eye, Crown, ShieldCheck, X, Send,
  Link2, UserCheck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { useActingAs } from "../context/ActingAsContext";
import LinkClientModal from "../components/LinkClientModal";

/**
 * Recruiter Dashboard — Resmo Pro · Talent Cloud · Phase 1
 *
 * Shows clients, Talent Cloud aggregate stats, and an "Add client" CTA.
 * Resmo Pro brand palette = deep navy #0A2540 + emerald #047857 + warm gold
 * #D4AF37 accents. Reads as banking/executive-search professional and is
 * deliberately distinct from candidate-side burnt-orange #FF6A00.
 */
const RECRUITER_NAVY = "#0A2540";
const RECRUITER_GREEN = "#047857";
const RECRUITER_GREEN_DEEP = "#064E3B";
const RECRUITER_GOLD = "#D4AF37";
// Keep RECRUITER_PURPLE / DEEP names so existing JSX can be repainted by
// retargeting two values rather than dozens of string-replace edits.
const RECRUITER_PURPLE = RECRUITER_NAVY;
const RECRUITER_DEEP = RECRUITER_GREEN;

export default function RecruiterDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [empathyMode, setEmpathyMode] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [statsResp, clientsResp] = await Promise.all([
        api.get("/recruiter/stats"),
        api.get("/recruiter/clients"),
      ]);
      setStats(statsResp.data);
      setClients(clientsResp.data.clients || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === null) return; // still loading
    if (user === false || !user?.is_recruiter) {
      navigate("/recruiter/signup");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || !user.is_recruiter) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          <div className="rm-card-static p-6 flex items-center gap-2"
               style={{ background: "#F8FAFC", borderColor: "#CBD5E1" }}>
            <Loader size={16} className="animate-spin" style={{ color: RECRUITER_PURPLE }} />
            <span className="font-mono-rm text-[11px] uppercase tracking-widest">Redirecting…</span>
          </div>
        </div>
      </div>
    );
  }

  // Empathy toggle — drop the recruiter into a read-only candidate-dashboard preview
  if (empathyMode) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
          <div className="rm-card-static p-5 mb-5"
               style={{ background: "#FEF3C7", borderColor: "#CA8A04", boxShadow: "4px 4px 0 0 #CA8A04" }}
               data-testid="empathy-mode-banner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                     style={{ color: "#78350F", fontWeight: 700 }}>
                  · Empathy mode · Candidate view
                </div>
                <div className="font-display uppercase text-base leading-tight"
                     style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                  You're seeing what your clients see.
                </div>
                <p className="text-[12px] mt-1" style={{ color: "#78350F" }}>
                  Walk the candidate experience to spot rough edges before submitting.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEmpathyMode(false)}
                className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                data-testid="empathy-mode-exit"
              >
                <X size={12} /> Exit empathy mode
              </button>
            </div>
          </div>
          <iframe
            src="/"
            title="Candidate dashboard preview"
            className="w-full rounded-md border-2"
            style={{ height: "75vh", borderColor: "#0F1B2D" }}
            data-testid="empathy-mode-frame"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper" data-testid="recruiter-dashboard-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                 style={{ color: RECRUITER_PURPLE, fontWeight: 700 }}>
              · Resmo Pro · Talent Cloud
            </div>
            <h1 className="font-display uppercase leading-none mb-2"
                style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}
                data-testid="recruiter-title">
              {stats?.agency || "Your agency"}{" "}
              <span style={{ color: RECRUITER_PURPLE }}>· recruiter desk.</span>
            </h1>
            <p className="text-sm md:text-base max-w-2xl" style={{ color: "#334155" }}>
              Manage your clients' tailored kits, browse the Talent Cloud waitlist,
              and submit candidates with the "Verified by Resmo" seal.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setEmpathyMode(true)}
              className="rm-btn rm-btn-ghost text-xs px-3 py-2"
              data-testid="recruiter-empathy-btn"
              title="See what your candidates see"
            >
              <Eye size={12} /> Empathy mode
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="rm-btn text-sm px-4 py-2.5"
              style={{
                background: RECRUITER_PURPLE, color: "white",
                border: `2px solid ${RECRUITER_DEEP}`,
                boxShadow: `4px 4px 0 0 ${RECRUITER_DEEP}`,
                fontWeight: 700,
              }}
              data-testid="recruiter-add-client-btn"
            >
              <Plus size={14} /> Add client
            </button>
          </div>
        </div>

        {error && (
          <div className="rm-card-static p-3 mb-4 text-xs font-mono-rm"
               style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}>
            · {error}
          </div>
        )}

        {loading ? (
          <div className="rm-card-static p-6 flex items-center gap-2"
               style={{ background: "#F8FAFC", borderColor: "#CBD5E1" }}>
            <Loader size={16} className="animate-spin" style={{ color: RECRUITER_PURPLE }} />
            <span className="font-mono-rm text-[11px] uppercase tracking-widest">Loading…</span>
          </div>
        ) : (
          <>
            {/* Stats tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatTile
                testid="stat-clients"
                label="Active clients"
                value={stats?.clients_count ?? 0}
                Icon={Users}
                color={RECRUITER_PURPLE}
              />
              <StatTile
                testid="stat-invited"
                label="Invited"
                value={stats?.invited_count ?? 0}
                Icon={Mail}
                color="#CA8A04"
              />
              <StatTile
                testid="stat-linked"
                label="Linked"
                value={stats?.linked_count ?? 0}
                Icon={ArrowRight}
                color="#047857"
              />
              <StatTile
                testid="stat-talent-cloud"
                label={`Talent Cloud · ${stats?.talent_cloud?.gold_tier ?? 0} gold`}
                value={stats?.talent_cloud?.total_opted_in ?? 0}
                Icon={Crown}
                color={RECRUITER_DEEP}
                hint="Coming soon"
              />
            </div>

            {/* Clients list */}
            <div className="rm-card-static p-5 mb-6"
                 style={{ background: "white", borderColor: "#0F1B2D", boxShadow: `4px 4px 0 0 ${RECRUITER_PURPLE}` }}
                 data-testid="recruiter-clients-card">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                     style={{ color: RECRUITER_DEEP, fontWeight: 700 }}>
                  · Your clients · {clients.length}
                </div>
                <button
                  type="button"
                  onClick={load}
                  className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                  data-testid="recruiter-refresh-btn"
                >
                  <RefreshCw size={11} /> Refresh
                </button>
              </div>
              {clients.length === 0 ? (
                <div className="text-center py-8" data-testid="recruiter-clients-empty">
                  <Briefcase size={32} style={{ color: "#CBD5E1", margin: "0 auto" }} />
                  <p className="text-sm mt-2" style={{ color: "#475569" }}>
                    No clients yet. Click <strong>Add client</strong> to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {clients.map((c) => (
                    <ClientRow key={c.id} client={c} onChanged={load} />
                  ))}
                </div>
              )}
            </div>

            {/* Matchmaker CTA — Phase 2 launch */}
            <RecruiterProUpgrade />
            <div className="rm-card-static p-5 mb-6"
                 style={{
                   background: `linear-gradient(135deg, ${RECRUITER_NAVY} 0%, #0F1B2D 50%, ${RECRUITER_GREEN_DEEP} 100%)`,
                   borderColor: RECRUITER_NAVY,
                   boxShadow: `5px 5px 0 0 ${RECRUITER_GOLD}`,
                 }}
                 data-testid="recruiter-matchmaker-cta">
              <div className="flex items-start gap-4 flex-wrap">
                <img
                  src="/resmo-pro-mascot.png"
                  alt="Resmo Pro mascot"
                  style={{ width: 96, height: 96, borderRadius: 8, border: `3px solid ${RECRUITER_GOLD}`, background: "#FBF7EE" }}
                  data-testid="recruiter-matchmaker-mascot"
                />
                <div className="flex-1 min-w-[220px]">
                  <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                       style={{ color: RECRUITER_GOLD, fontWeight: 700 }}>
                    · NEW · Resmo Pro Matchmaker
                  </div>
                  <h3 className="font-display uppercase leading-tight"
                      style={{ fontSize: "clamp(1.1rem, 2vw, 1.5rem)", color: "white", letterSpacing: "-0.01em" }}>
                    Post a requisition. <span style={{ color: "#A7F3D0" }}>Resmo matches</span> opted-in candidates.
                  </h3>
                  <p className="text-[13px] mt-1 leading-snug" style={{ color: "#CBD5E1" }}>
                    Permission-first hiring: candidates control whether you ever
                    reach out. Each requisition scores every opted-in profile
                    by tier, skill overlap, role fit, and recency.
                    <span style={{ color: RECRUITER_GOLD, fontWeight: 700 }}> 50 credits</span> per match run.
                  </p>
                </div>
                <Link
                  to="/recruiter/matchmaker"
                  className="rm-btn text-sm px-4 py-2.5"
                  style={{
                    background: RECRUITER_GREEN, color: "white",
                    border: `2px solid ${RECRUITER_GREEN_DEEP}`,
                    boxShadow: `3px 3px 0 0 ${RECRUITER_GOLD}`,
                    fontWeight: 700,
                  }}
                  data-testid="recruiter-open-matchmaker-btn"
                >
                  <Sparkles size={14} /> Open Matchmaker <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Talent Cloud waitlist callout */}
            <div className="rm-card-static p-5"
                 style={{
                   background: `linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)`,
                   borderColor: RECRUITER_GREEN,
                   boxShadow: `4px 4px 0 0 ${RECRUITER_NAVY}`,
                 }}
                 data-testid="recruiter-talent-cloud-card">
              <div className="flex items-start gap-3 flex-wrap">
                <Crown size={22} style={{ color: RECRUITER_GOLD, flexShrink: 0, marginTop: 2 }} />
                <div className="flex-1 min-w-[220px]">
                  <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                       style={{ color: RECRUITER_DEEP, fontWeight: 700 }}>
                    · Talent Cloud · Coming Q3
                  </div>
                  <h3 className="font-display uppercase text-lg leading-tight mb-1"
                      style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                    {stats?.talent_cloud?.total_opted_in ?? 0} candidates already opted in.
                  </h3>
                  <p className="text-sm leading-snug" style={{ color: "#334155" }}>
                    Search verified candidates with Resmo's Honesty Seal. Permission-based,
                    spam-free outreach, ATS-verified resumes. Tier 1 (Recruiter-as-Coach)
                    is live now — Talent Cloud unlocks when we hit critical mass.
                  </p>
                </div>
                <a
                  href="mailto:hello@resmo.app?subject=Talent%20Cloud%20early%20access"
                  className="rm-btn text-xs px-3 py-2"
                  style={{
                    background: "white", color: RECRUITER_DEEP,
                    border: `2px solid ${RECRUITER_PURPLE}`,
                  }}
                  data-testid="recruiter-talent-cloud-waitlist-btn"
                >
                  <Send size={11} /> Get early access
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      <AddClientModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={(created) => {
          setShowAdd(false);
          setClients((prev) => [created, ...prev]);
        }}
      />
    </div>
  );
}

/* ─── Helpers ─── */

function StatTile({ label, value, Icon, color, hint, testid }) {
  return (
    <div
      className="rm-card-static p-3 md:p-4"
      style={{ background: "white", borderColor: "#0F1B2D", boxShadow: `3px 3px 0 0 ${color}` }}
      data-testid={testid}
    >
      <div className="flex items-start gap-2">
        <Icon size={18} style={{ color, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="font-mono-rm uppercase text-[9px] tracking-widest leading-tight"
               style={{ color: "#475569", fontWeight: 700 }}>
            {label}
          </div>
          <div className="font-display text-3xl leading-none mt-1"
               style={{ color: "#0F1B2D" }}>
            {value}
          </div>
          {hint && (
            <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-1"
                 style={{ color: "#94A3B8" }}>
              · {hint}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientRow({ client, onChanged }) {
  const navigate = useNavigate();
  const { actingAs, setActingAs } = useActingAs();
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const isLinked = client.status === "linked" && client.candidate_user_id;
  const isCurrentlyActing = actingAs?.client_id === client.id;

  const handleDelete = async () => {
    if (!window.confirm(`Archive ${client.name}? This soft-deletes them; data is retained.`)) return;
    setBusy(true);
    try {
      await api.delete(`/recruiter/clients/${client.id}`);
      onChanged?.();
    } catch (_e) { /* silent */ }
    finally { setBusy(false); }
  };
  const handleResendInvite = async () => {
    setBusy(true);
    try {
      await api.post(`/recruiter/clients/${client.id}/invite`);
      onChanged?.();
    } catch (_e) { /* silent */ }
    finally { setBusy(false); }
  };
  const handleActAs = () => {
    setActingAs({
      client_id: client.id,
      client_name: client.candidate_name || client.name,
      client_email: client.candidate_email || client.email,
      candidate_user_id: client.candidate_user_id,
    });
    navigate("/");  // jump to landing under the client's identity
  };
  const handleLinked = (info) => {
    setLinkOpen(false);
    onChanged?.();
    // Immediately offer to enter acting-as mode
    setActingAs(info);
    navigate("/");
  };
  return (
    <div className="rm-card-static p-3 flex items-center gap-3 flex-wrap"
         style={{
           background: isCurrentlyActing ? "#ECFDF5" : "#F8FAFC",
           borderColor: isCurrentlyActing ? "#047857" : "#E2E8F0",
         }}
         data-testid={`recruiter-client-${client.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-display uppercase text-sm leading-tight"
             style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
          {client.name}
        </div>
        {client.role_target && (
          <div className="font-mono-rm text-[10px] uppercase tracking-widest"
               style={{ color: "#475569" }}>
            · Target · {client.role_target}
          </div>
        )}
        {client.email && (
          <div className="text-[11.5px]" style={{ color: "#94A3B8" }}>
            {client.email}
          </div>
        )}
      </div>
      <StatusPill status={client.status} />
      <div className="flex items-center gap-1.5 flex-wrap">
        {isLinked ? (
          <button
            type="button"
            onClick={handleActAs}
            disabled={busy || isCurrentlyActing}
            className="rm-btn text-[10px] px-2.5 py-1"
            style={{
              background: isCurrentlyActing ? "#D1FAE5" : "#047857",
              color: isCurrentlyActing ? "#064E3B" : "white",
              border: "2px solid #064E3B",
              fontWeight: 700,
            }}
            data-testid={`recruiter-act-as-${client.id}`}
            title={isCurrentlyActing ? "You're already acting as this client" : "Run the toolkit on this client's account"}
          >
            <UserCheck size={11} /> {isCurrentlyActing ? "Acting" : "Act as"}
          </button>
        ) : client.email ? (
          <button
            type="button"
            onClick={() => setLinkOpen(true)}
            disabled={busy}
            className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
            style={{ color: "#047857", borderColor: "#047857" }}
            data-testid={`recruiter-link-client-${client.id}`}
            title="Link to candidate's Resmo account"
          >
            <Link2 size={11} /> Link
          </button>
        ) : null}
        {client.email && client.status !== "linked" && (
          <button
            type="button"
            onClick={handleResendInvite}
            disabled={busy}
            className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
            data-testid={`recruiter-resend-invite-${client.id}`}
          >
            <Mail size={11} /> {client.status === "invited" ? "Resend" : "Invite"}
          </button>
        )}
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
          style={{ color: "#B91C1C", borderColor: "#B91C1C" }}
          data-testid={`recruiter-delete-client-${client.id}`}
        >
          <Trash2 size={11} />
        </button>
      </div>

      <LinkClientModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        client={client}
        onLinked={handleLinked}
      />
    </div>
  );
}

function StatusPill({ status }) {
  const palette = {
    manual:   { bg: "#FEF3C7", color: "#78350F", label: "Manual" },
    invited:  { bg: "#DBEAFE", color: "#1E40AF", label: "Invited" },
    linked:   { bg: "#D1FAE5", color: "#047857", label: "Linked" },
    archived: { bg: "#F1F5F9", color: "#64748B", label: "Archived" },
  }[status] || { bg: "#F1F5F9", color: "#64748B", label: status || "—" };
  return (
    <span
      className="font-mono-rm text-[9.5px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-0.5 rounded"
      style={{ background: palette.bg, color: palette.color, fontWeight: 700 }}
      data-testid={`client-status-${status}`}
    >
      · {palette.label}
    </span>
  );
}

function AddClientModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleTarget, setRoleTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [sendInvite, setSendInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(""); setEmail(""); setRoleTarget(""); setNotes("");
      setSendInvite(false); setError("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name) { setError("Name is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/recruiter/clients", {
        name, email: email || null, role_target: roleTarget || null,
        notes: notes || null, send_invite: sendInvite,
      });
      onCreated?.(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't add client.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(15, 27, 45, 0.55)" }}
         onClick={onClose}
         data-testid="add-client-modal">
      <div className="rm-card-static p-6 md:p-8 w-full max-w-lg"
           onClick={(e) => e.stopPropagation()}
           style={{ background: "white" }}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                 style={{ color: RECRUITER_PURPLE, fontWeight: 700 }}>
              · Resmo Pro · Add client
            </div>
            <h2 className="font-display uppercase text-2xl leading-tight"
                style={{ color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              New <span style={{ color: RECRUITER_PURPLE }}>client.</span>
            </h2>
            <p className="text-[12px] mt-1" style={{ color: "#475569" }}>
              Manual entry for prep work, or email invite to hand off when ready.
            </p>
          </div>
          <button onClick={onClose} className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                  data-testid="add-client-close">
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="add-client-form">
          <div>
            <label className="rm-label text-[11px]">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   className="rm-input w-full" placeholder="Alex Johnson"
                   data-testid="add-client-name" required />
          </div>
          <div>
            <label className="rm-label text-[11px]">Email (optional · required for invite)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="rm-input w-full" placeholder="alex@example.com"
                   data-testid="add-client-email" />
          </div>
          <div>
            <label className="rm-label text-[11px]">Target role (optional)</label>
            <input value={roleTarget} onChange={(e) => setRoleTarget(e.target.value)}
                   className="rm-input w-full" placeholder="Senior Product Manager"
                   data-testid="add-client-role" />
          </div>
          <div>
            <label className="rm-label text-[11px]">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2} className="rm-input w-full"
                      placeholder="Remote-only · Open to senior IC · Notice 4 weeks"
                      data-testid="add-client-notes" />
          </div>
          {email && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={sendInvite}
                     onChange={(e) => setSendInvite(e.target.checked)}
                     data-testid="add-client-send-invite" />
              <span className="text-[12.5px]" style={{ color: "#334155" }}>
                Send the candidate an invite email now
              </span>
            </label>
          )}
          {error && (
            <div className="rm-card-static p-2 text-xs font-mono-rm"
                 style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                 data-testid="add-client-error">
              · {error}
            </div>
          )}
          <div className="flex justify-end gap-2 flex-wrap">
            <button type="button" onClick={onClose}
                    className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                    data-testid="add-client-cancel">
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    className="rm-btn text-sm px-4 py-2.5"
                    style={{
                      background: RECRUITER_PURPLE, color: "white",
                      border: `2px solid ${RECRUITER_DEEP}`,
                      boxShadow: `3px 3px 0 0 ${RECRUITER_DEEP}`,
                      fontWeight: 700,
                    }}
                    data-testid="add-client-submit">
              {busy ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
              {busy ? "Saving…" : "Add client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────  Resmo Pro · $59/mo Stripe upgrade  ───────────────────── */

function RecruiterProUpgrade() {
  const { billing, refresh } = useBilling();
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    // Auto-refresh billing state when user lands here after a Stripe redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id")) {
      // Poll fulfillment endpoint a few times (webhook may be late)
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        try {
          await api.get("/billing/checkout/status", { params: { session_id: params.get("session_id") } });
        } catch { /* ignore */ }
        await refresh();
        if (attempts < 6) setTimeout(tick, 1500);
      };
      tick();
    }
  }, [refresh]);

  const isPro = !!billing?.is_recruiter_pro;
  const expiresAt = billing?.recruiter_pro_expires_at;

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        kind: "recruiter_pro",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed — try again.");
    }
  };

  if (isPro) {
    return (
      <div
        className="rm-card-static p-3 mb-4 flex items-center gap-3 flex-wrap"
        style={{
          background: "#ECFDF5", borderColor: RECRUITER_GREEN,
          boxShadow: `3px 3px 0 0 ${RECRUITER_GREEN_DEEP}`,
        }}
        data-testid="recruiter-pro-active-pill"
      >
        <Crown size={18} style={{ color: RECRUITER_GOLD, flexShrink: 0 }} />
        <span className="font-mono-rm uppercase text-[10px] tracking-widest"
              style={{ color: RECRUITER_GREEN_DEEP, fontWeight: 700 }}>
          · Resmo Pro · Recruiter · Active
        </span>
        {expiresAt && (
          <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: "#475569" }}>
            · Renews {new Date(expiresAt).toLocaleDateString()}
          </span>
        )}
        <span className="flex-1" />
        <Link to="/billing"
              className="font-mono-rm text-[10px] uppercase tracking-widest"
              style={{ color: RECRUITER_NAVY, textDecoration: "underline", fontWeight: 700 }}
              data-testid="recruiter-pro-manage-link">
          Manage billing →
        </Link>
      </div>
    );
  }

  return (
    <div
      className="rm-card-static p-4 mb-4 flex items-center gap-3 flex-wrap"
      style={{
        background: `linear-gradient(135deg, #FBF7EE 0%, #FEF3C7 100%)`,
        borderColor: RECRUITER_NAVY,
        boxShadow: `3px 3px 0 0 ${RECRUITER_GOLD}`,
      }}
      data-testid="recruiter-pro-upgrade-card"
    >
      <Crown size={22} style={{ color: RECRUITER_GOLD, flexShrink: 0 }} />
      <div className="flex-1 min-w-[180px]">
        <div className="font-mono-rm uppercase text-[10px] tracking-widest"
             style={{ color: RECRUITER_NAVY, fontWeight: 700 }}>
          · Resmo Pro · Recruiter · $59/mo
        </div>
        <p className="text-sm leading-snug mt-0.5" style={{ color: RECRUITER_NAVY }}>
          Unlimited Matchmaker runs, the full client roster, Acting-on-behalf, and all candidate-Pro features bundled in.
        </p>
      </div>
      <button
        type="button"
        onClick={upgrade}
        disabled={busy}
        className="rm-btn text-sm px-4 py-2"
        style={{
          background: RECRUITER_NAVY, color: "white",
          border: `2px solid ${RECRUITER_GREEN_DEEP}`,
          boxShadow: `3px 3px 0 0 ${RECRUITER_GOLD}`, fontWeight: 700,
        }}
        data-testid="recruiter-pro-upgrade-btn"
      >
        {busy ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {busy ? "Redirecting…" : "Upgrade"} <ArrowRight size={14} />
      </button>
    </div>
  );
}
